import { forwardRef } from 'react';

/**
 * Shareable story card, rendered as real DOM so html-to-image can rasterize
 * it faithfully. Two aspects:
 *   - 'square' (1080×1080) for Instagram feed posts, WhatsApp messages, etc.
 *   - 'story'  (1080×1920) for Instagram Stories / Reels covers, WhatsApp
 *     Status, YouTube Shorts thumbnails.
 *
 * Intrinsic size is fixed by aspect; callers scale via `previewScale` for
 * on-screen display while download captures the full-size unscaled node.
 */

export const STORY_CARD_THEMES = [
  { id: 'gradient', label: 'Gradient' },
  { id: 'minimal', label: 'Minimal' },
  { id: 'vibrant', label: 'Vibrant' },
  { id: 'dark', label: 'Dark' },
];

export const STORY_CARD_ASPECTS = {
  square: { id: 'square', label: 'Post', width: 1080, height: 1080, ratio: '1 / 1' },
  story: { id: 'story', label: 'Story', width: 1080, height: 1920, ratio: '9 / 16' },
};

const THEME_STYLES = {
  gradient: {
    background: 'linear-gradient(135deg, #C05621 0%, #E8915B 55%, #FDFBF7 100%)',
    text: '#2D2A26',
    subtle: '#5A3F2E',
    accent: '#FFFFFF',
    quoteBg: 'rgba(255, 255, 255, 0.55)',
    quoteText: '#2D2A26',
    border: 'transparent',
  },
  minimal: {
    background: '#FDFBF7',
    text: '#2D2A26',
    subtle: '#6B665E',
    accent: '#C05621',
    quoteBg: '#FBF4EB',
    quoteText: '#2D2A26',
    border: '#E6E2D6',
  },
  vibrant: {
    background: '#4A5D4F',
    text: '#FDFBF7',
    subtle: '#D6DCC8',
    accent: '#F5C16C',
    quoteBg: 'rgba(253, 251, 247, 0.12)',
    quoteText: '#FDFBF7',
    border: 'transparent',
  },
  dark: {
    background: '#2D2A26',
    text: '#FDFBF7',
    subtle: '#C9C2B2',
    accent: '#E8915B',
    quoteBg: 'rgba(253, 251, 247, 0.08)',
    quoteText: '#FDFBF7',
    border: 'transparent',
  },
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

// Minor prepositions / conjunctions that stay lowercase in title case
// (except as the first word, which is always capitalised).
const LOWER_WORDS = new Set([
  'a', 'an', 'the', 'and', 'but', 'or', 'nor', 'for', 'so', 'yet',
  'at', 'by', 'in', 'of', 'on', 'to', 'up', 'as', 'is', 'it', 'its',
]);

/**
 * Title-cases a string while keeping short prepositions/conjunctions lowercase.
 * Designed for the "A story of X & Y" subtitle produced by formatFromTo.
 */
const toCardTitleCase = (str) => {
  if (!str) return str;
  return str
    .split(' ')
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (i > 0 && LOWER_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

const GENERIC_CATEGORIES = new Set(['other', 'general', 'story']);

const isShowableCategory = (raw) => {
  if (!raw) return false;
  return !GENERIC_CATEGORIES.has(String(raw).toLowerCase());
};

const StoryCard = forwardRef(function StoryCard(
  {
    story,
    theme = 'gradient',
    aspect = 'square',
    pullQuote = '',
    showAuthor = true,
    showPullQuote = true,
    showQr = true,
    qrDataUrl = '',
    brandLogoUrl = '',
    fromTo = '',
    professionLabel = '',
    previewScale = 1,
  },
  ref
) {
  const t = THEME_STYLES[theme] || THEME_STYLES.gradient;
  const dims = STORY_CARD_ASPECTS[aspect] || STORY_CARD_ASPECTS.square;
  const W = dims.width;
  const H = dims.height;
  const PAD = aspect === 'story' ? 88 : 72;
  const isStory = aspect === 'story';

  const author = showAuthor && story?.author_name ? story.author_name : '';
  const date = formatDate(story?.created_at);
  const title = story?.title || 'A story';
  const rawCategory = story?.tradition || story?.category || '';
  const category = isShowableCategory(rawCategory) ? rawCategory : '';

  // 9:16 has 78% more vertical pixels than 1:1 but the *width* is the same
  // (1080), so title tiers are about character count fitting horizontally.
  // Pick the largest size that won't make a single line overflow ~904px of
  // content area (1080 - padding * 2).
  const titleSize = isStory
    ? (title.length > 45 ? 68 : title.length > 25 ? 84 : 104)
    : (title.length > 60 ? 64 : 80);
  const fromToSize = isStory ? 36 : 28;
  const quoteSize = isStory ? 44 : 34;
  const authorSize = isStory ? 40 : 32;
  const dateSize = isStory ? 28 : 22;
  const headerSize = isStory ? 30 : 26;
  const qrSize = isStory ? 220 : 180;
  // Emblem anchors the header lockup. Sized well above the wordmark cap
  // height so it reads as a brand mark, not a bullet.
  const logoSize = isStory ? 96 : 76;

  return (
    <div
      style={{
        width: W,
        height: H,
        boxSizing: 'border-box',
        transform: previewScale === 1 ? undefined : `scale(${previewScale})`,
        transformOrigin: 'top left',
      }}
    >
      <div
        ref={ref}
        style={{
          width: W,
          height: H,
          padding: PAD,
          background: t.background,
          color: t.text,
          border: t.border !== 'transparent' ? `1px solid ${t.border}` : 'none',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Brand lockup — emblem + wordmark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isStory ? 20 : 16, minWidth: 0 }}>
            {brandLogoUrl && (
              <img
                src={brandLogoUrl}
                alt=""
                width={logoSize}
                height={logoSize}
                style={{ display: 'block', flexShrink: 0, borderRadius: '50%' }}
              />
            )}
            <span
              style={{
                fontSize: headerSize,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: 'uppercase',
                color: t.subtle,
              }}
            >
              Katha Mithra
            </span>
          </div>

          {/* Category pill — styled as a contained tag so it doesn't float */}
          {category && (
            <span
              style={{
                fontSize: isStory ? 24 : 20,
                fontWeight: 500,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                color: t.subtle,
                border: `1.5px solid ${t.subtle}`,
                borderRadius: 999,
                padding: isStory ? '8px 28px' : '6px 22px',
                opacity: 0.75,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {category}
            </span>
          )}
        </div>

        {/* Title + tagline + quote */}
        <div
          style={{
            marginTop: isStory ? 96 : 64,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isStory ? 'flex-start' : 'flex-start',
          }}
        >
          <div
            style={{
              fontFamily: '"Lora", "Georgia", serif',
              fontWeight: 500,
              fontSize: titleSize,
              lineHeight: 1.1,
              color: t.text,
              letterSpacing: -1,
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {title}
          </div>

          {fromTo && (
            <div
              style={{
                marginTop: isStory ? 40 : 28,
                fontSize: fromToSize,
                fontWeight: 500,
                color: t.accent,
              }}
            >
              {toCardTitleCase(fromTo)}
            </div>
          )}

          {showPullQuote && pullQuote && (
            <div
              style={{
                marginTop: isStory ? 72 : 48,
                padding: isStory ? '56px 60px' : '44px 48px',
                background: t.quoteBg,
                borderRadius: isStory ? 36 : 28,
                color: t.quoteText,
                fontFamily: '"Lora", "Georgia", serif',
                fontStyle: 'italic',
                fontSize: quoteSize,
                lineHeight: 1.6,
                boxSizing: 'border-box',
                width: '100%',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              <span
                style={{
                  fontSize: isStory ? 72 : 56,
                  lineHeight: 0,
                  verticalAlign: '-0.3em',
                  marginRight: 8,
                }}
              >
                “
              </span>
              {pullQuote}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: isStory ? 72 : 48,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 32,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            {/* Author / profession / date — tight cluster */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {author && (
                <div style={{ fontSize: authorSize, fontWeight: 600, color: t.text }}>
                  {author}
                </div>
              )}
              {/* Profession sits just below the author name — same demographic
                  context the search-results cards show. Only rendered
                  when showAuthor is on and we actually have a label,
                  so anonymous/unknown stories stay clean. */}
              {showAuthor && professionLabel && (
                <div style={{ fontSize: dateSize, color: t.subtle }}>
                  {professionLabel}
                </div>
              )}
              {date && (
                <div style={{ fontSize: dateSize, color: t.subtle }}>{date}</div>
              )}
            </div>

            {/* CTA — separated so it reads as its own line */}
            <div
              style={{
                marginTop: isStory ? 28 : 20,
                fontSize: dateSize,
                fontWeight: 500,
                color: t.subtle,
              }}
            >
              Scan to read on Katha Mithra
            </div>
          </div>

          {showQr && qrDataUrl && (
            <div
              style={{
                background: '#FFFFFF',
                padding: isStory ? 22 : 18,
                borderRadius: isStory ? 22 : 18,
                lineHeight: 0,
                flexShrink: 0,
                boxSizing: 'border-box',
              }}
            >
              <img
                src={qrDataUrl}
                alt=""
                width={qrSize}
                height={qrSize}
                style={{ display: 'block' }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default StoryCard;
